/**
 * An override conflict on Pending Review (William's rule, 19 Oct 2026): Lightspeed
 * changed a field a Vaperite admin had overridden. The reviewer sees what Lightspeed
 * had, what it has now and their own value, and decides:
 *
 *  - Take Lightspeed → POST field-changes/:id/approve (writes Lightspeed's value,
 *    drops the override)
 *  - Keep mine       → POST field-changes/:id/keep-override (Lightspeed's new value
 *    becomes the stored source, the override stays)
 *
 * A 409 means someone or something resolved it first, or Lightspeed moved again:
 * the server's message is shown and the caller refreshes.
 */
import React, { useState } from 'react';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import { stagingApiBase } from './stagingOrg';

export interface OverrideConflictChange {
  id: number | null;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  overrideValue?: string | null;
  overrideConflict?: boolean;
  /** Category conflicts: the names for the stored category ids. */
  displayValues?: { old: string | null; new: string | null; override: string | null };
}

export type OverrideConflictOutcome = 'take' | 'keep' | 'stale';

interface Props {
  change: OverrideConflictChange;
  /** Resolved (or found stale): the caller refreshes the item. */
  onResolved: (outcome: OverrideConflictOutcome) => void;
  onError: (message: string) => void;
}

function show(fieldName: string, value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  // Lightspeed's side is the woocommerce tag; ours is Sell in app.
  if (fieldName === 'sellable') return value === 'true' ? 'On' : 'Off';
  if (fieldName === 'price') {
    const n = Number(value);
    return Number.isFinite(n) ? `R${n.toFixed(2)}` : value;
  }
  return value;
}

const OverrideConflict: React.FC<Props> = ({ change, onResolved, onError }) => {
  const { colors } = useAdminTheme();
  const [busy, setBusy] = useState<OverrideConflictOutcome | null>(null);
  const d = change.displayValues;
  const values: Array<{ label: string; value: string | null | undefined; color: string }> = [
    { label: 'Lightspeed was', value: d ? d.old : change.oldValue, color: colors.textMuted },
    { label: 'Lightspeed now', value: d ? d.new : change.newValue, color: colors.success },
    { label: 'Your value', value: d ? d.override : change.overrideValue, color: colors.info },
  ];

  const resolve = async (outcome: 'take' | 'keep') => {
    if (change.id == null) return;
    setBusy(outcome);
    try {
      if (outcome === 'take') {
        await authInstance.post(`${stagingApiBase()}/staging/field-changes/${change.id}/approve`, {});
      } else {
        await authInstance.post(`${stagingApiBase()}/staging/field-changes/${change.id}/keep-override`);
      }
      onResolved(outcome);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to resolve the conflict';
      onError(message);
      // Already resolved elsewhere, or Lightspeed moved again: show the current state.
      if (err?.response?.status === 409) onResolved('stale');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
      {values.map((v) => (
        <Box key={v.label} sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', color: colors.textMuted, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            {v.label}
          </Typography>
          <Typography variant="body2" sx={{ color: v.color, fontWeight: 600, maxWidth: 220 }} noWrap title={v.value ?? ''}>
            {show(change.fieldName, v.value)}
          </Typography>
        </Box>
      ))}
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={busy !== null || change.id == null}
          onClick={() => resolve('take')}
          sx={{ borderColor: alpha(colors.success, 0.5), color: colors.success, textTransform: 'none', fontWeight: 700 }}
        >
          Take Lightspeed
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={busy !== null || change.id == null}
          onClick={() => resolve('keep')}
          sx={{ borderColor: alpha(colors.info, 0.5), color: colors.info, textTransform: 'none', fontWeight: 700 }}
        >
          Keep mine
        </Button>
      </Stack>
    </Stack>
  );
};

export default OverrideConflict;
